import type { ReactNode } from 'react'
import {
  useFeatureFlag,
  type FeatureFlagKey,
} from '@/admin/hooks/use-feature-flag'

interface FeatureGateProps {
  /** The feature flag key to check against the current tenant. */
  flag: FeatureFlagKey
  /** Content rendered when the flag is enabled. */
  children: ReactNode
  /** Content rendered when the flag is disabled (defaults to nothing). */
  fallback?: ReactNode
}

/**
 * Renders children only when the specified feature flag is enabled for
 * the current tenant.  When disabled, renders the optional fallback
 * (defaults to `null`).
 *
 * @example
 *   <FeatureGate flag="transfers">
 *     <TransferWizard />
 *   </FeatureGate>
 *
 * @example
 *   <FeatureGate flag="proration" fallback={<UpgradeBanner />}>
 *     <ProrationPanel />
 *   </FeatureGate>
 */
export function FeatureGate({
  flag,
  children,
  fallback = null,
}: FeatureGateProps) {
  const enabled = useFeatureFlag(flag)
  return enabled ? <>{children}</> : <>{fallback}</>
}
